import React, { useState } from 'react';
import { Linking, Pressable, StyleSheet, Text, View } from 'react-native';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../../services/apiService';
import { writeBillingSnapshot } from '../../utils/billingSnapshot';
import { trackBillingAction } from '../../utils/billingAnalytics';
import { usePostHog } from 'posthog-js/react';

export default function BillingModalPaymentMethod({ summary, source }) {
  const posthog = usePostHog();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  if (!summary) return null;
  // Step 8: a carded user (trial_carded) has a card on file but the carded
  // summary does not fetch the saved-card details (paymentMethod is null). They
  // still get the SAME "Update payment method" affordance a charged Pro user
  // has, reusing the exact billing-action update_card -> Paddle portal call
  // below (R16.4 reuse: no bespoke carded card-update surface). The replacement
  // card does not change next_billed_at or the trialing state.
  const isCardOnFileOnly = !summary.paymentMethod && summary.status === 'trial_carded';

  // Free / pre-card trial users don't have a payment method row and no card on
  // file, so there is nothing to manage.
  if (!summary.paymentMethod && !isCardOnFileOnly) return null;

  const { brand, last4, expMonth, expYear } = summary.paymentMethod || {};

  const handleUpdateCard = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    const started = Date.now();

    trackBillingAction(posthog, 'billing_action_clicked', { action: 'update_card', status: summary.status, source });

    try {
      writeBillingSnapshot(summary);
      const idempotencyKey = crypto.randomUUID();
      const body = await apiService.invokeEdgeFunction('billing-action', {
        body: { action: 'update_card' },
        headers: { 'Idempotency-Key': idempotencyKey },
      });

      trackBillingAction(posthog, 'billing_action_completed', {
        action: 'update_card', status: summary.status, source,
        duration_ms: Date.now() - started,
      });

      if (body.portalUrl) {
        try { window.open(body.portalUrl, '_blank', 'noopener,noreferrer'); }
        catch { Linking.openURL(body.portalUrl); }
      }
    } catch (e) {
      trackBillingAction(posthog, 'billing_action_failed', {
        action: 'update_card', status: summary.status, source,
        duration_ms: Date.now() - started, error_code: e?.code ?? e?.message,
      });
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModalPaymentMethod', action: 'update_card',
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: summary?.status } },
      });
      setError(e?.code || e?.message || 'portal_failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <View style={styles.root}>
      <Text style={styles.label}>Payment method</Text>
      <View style={styles.row}>
        <Text style={styles.cardText}>
          {isCardOnFileOnly
            ? 'Card on file'
            : `${formatBrand(brand)} •••• ${last4}  ·  exp ${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`}
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={handleUpdateCard}
          disabled={pending}
          style={({ pressed }) => [styles.btn, pressed && { opacity: 0.7 }]}
        >
          <Text style={styles.btnText}>{pending ? 'Opening...' : 'Update payment method'}</Text>
        </Pressable>
      </View>
      {error && <Text role="alert" style={styles.error}>Could not open payment portal. Please try again.</Text>}
    </View>
  );
}

function formatBrand(brand) {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 12, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)' },
  label: { fontSize: 12, fontWeight: '600', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', letterSpacing: 0.6 },
  row: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  cardText: { fontSize: 14, color: '#FFFFFF' },
  btn: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)', backgroundColor: 'transparent' },
  btnText: { fontSize: 13, fontWeight: '600', color: '#FFFFFF' },
  error: { marginTop: 8, fontSize: 13, color: '#FF6B6B' },
});
